import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { kioskCommand } from 'api/Kiosk';

import { ia } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';

const commandForProcessor = (processor) =>
  processor === 'Payroc' ? 'payroc-bt-select' : 'elavon-select-terminal';

const KioskTerminalSelect = ({ data, kioskName, onClose }) => {
  const navigate = useNavigate();
  const [pendingIndex, setPendingIndex] = useState(null);

  const devices = ia(data?.devices) ? data.devices : [];

  const { mutate: selectTerminal, isPending } = useCustomMutation({
    mutationFn: ({ deviceIndex }) =>
      kioskCommand(navigate, {
        command: commandForProcessor(data?.processor),
        kioskId: data?.kioskId,
        deviceIndex
      }),
    successMessage: 'Terminal selection sent',
    errorMessage: 'There was a problem selecting the terminal',
    onSuccess: onClose,
    onError: () => setPendingIndex(null)
  });

  const handleSelect = (deviceIndex) => {
    setPendingIndex(deviceIndex);
    selectTerminal({ deviceIndex });
  };

  const titleSuffix = kioskName ? ` for ${kioskName}` : '';

  return (
    <Modal
      isOpen={!!data}
      title={`Select a terminal${titleSuffix}`}
      handleClose={isPending ? undefined : onClose}
      width="small"
      slideFromBottom
      overlayClassName="items-end"
      bodyClassName="h-fit"
      footer={
        <div className="flex w-full justify-end">
          <Button
            text="Cancel"
            variant="tertiary"
            onClick={onClose}
            isDisabled={isPending}
            size="small"
          />
        </div>
      }>
      <div className="flex flex-col gap-2 px-1">
        <p className="text-sm text-neutral-700">
          Multiple terminals were found. Choose which one to pair.
        </p>
        {devices.length === 0 ? (
          <p className="py-4 text-center text-sm text-neutral-500">No terminals available.</p>
        ) : (
          devices.map((device) => {
            const isThisPending = isPending && pendingIndex === device.index;
            return (
              <button
                key={device.index}
                type="button"
                disabled={isPending}
                onClick={() => handleSelect(device.index)}
                className="flex items-center justify-between rounded-md border border-solid border-neutral-200 bg-white px-3 py-3 text-left transition-colors hover:bg-neutral-75 disabled:cursor-not-allowed disabled:opacity-60">
                <div className="flex flex-col">
                  <span className="font-500 text-neutral-900">{device.name || 'Unknown'}</span>
                  {data?.processor && (
                    <span className="text-xs text-neutral-500">{data.processor}</span>
                  )}
                </div>
                <Icon
                  icon={isThisPending ? 'loader-mono' : 'arrow-right-1-mono'}
                  size={16}
                  color="neutral"
                />
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default KioskTerminalSelect;
